import { useRef } from 'react';

import { useRoutines } from '../../../lib/routines';
import { useTasks } from '../../../lib/tasks';
import { useWorkspaceGoals } from '../../../lib/goals';

// Whether the summary tiles (Tasks · Routines · Files · Apps · Goals) can be drawn as one row.
//
// **Three of the five render NOTHING until their read lands** — `TasksSummary`, `RoutinesSummary` and
// `GoalsSummary` all `return null` unless their state is `ready`, while the Files and Apps tiles paint
// immediately off a fallback blurb. So without a gate the row assembles itself in front of the reader:
// tiles arriving one at a time, each shoving the rest along. Tasks, routines and goals are separate
// queries, so it can happen three times.
//
// The gate is on `loading` specifically, not on `ready`: a FAILED read must let the row through, or one
// dead query hides the other tiles forever. A tile whose read failed simply stays out.
//
// Every caller shares these three queries with the tiles themselves — React Query dedupes them, so the
// gate costs no extra request. Pass the Space's id on a Space-scoped row so the gate waits for the same
// scoped reads its tiles will show.
//
// **Once the row is shown it stays shown** (per scope). The gate is for the first paint only: a read that
// failed with no data refetches whenever a tile using it MOUNTS, and reports `loading` again while it does.
// Re-applying the gate then hid the row, which unmounted the tiles, which let the read fail, which showed the
// row, which mounted the tiles: a loop at the speed of the network. One browser whose reads all failed sent
// ~8 requests a second for hours that way (2026-10-08). A scope not shown yet (another Space) waits again.
export function useSummaryTilesReady(workspaceId?: string): boolean {
  const { state: tasks } = useTasks(workspaceId);
  const { state: routines } = useRoutines(workspaceId);
  const goals = useWorkspaceGoals(workspaceId);
  const settled = tasks.kind !== 'loading' && routines.kind !== 'loading' && goals.status !== 'loading';
  // Every scope shown so far, not just the last: the dashboard host keeps this mounted across Spaces, so
  // A → B → A returns to a Space whose failed read may be retrying right then.
  const shownScopes = useRef(new Set<string>());
  const scope = workspaceId ?? '';
  if (settled) shownScopes.current.add(scope);
  return settled || shownScopes.current.has(scope);
}
